import { useState, useMemo } from 'react';
import {
  Modal, Stack, TextInput, ScrollArea, Group, Text, Button, Checkbox, Badge, Box, SegmentedControl,
  Center, Loader,
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import fuzzysort from 'fuzzysort';
import { bcLong } from '@lib2/model/util/string/BaseConverter';
import { usePlaylistMedia } from '../usePlaylistMedia';
import { PlaylistUtils } from '../PlaylistUtils';
import {
  PLAYLIST_ADD_POSITIONS,
  PLAYLIST_ADD_POSITION_LABELS,
  type PlaylistAddPosition,
} from '@/lib/constants/playlist';
import type { MediaType } from '@/lib/types/Media';

const ADD_POSITION_OPTIONS = PLAYLIST_ADD_POSITIONS.map(value => ({
  value,
  label: PLAYLIST_ADD_POSITION_LABELS[value],
}));

interface MediaPickerModalProps {
  opened: boolean;
  onClose: () => void;
  onAdd: (mediaIds: number[], addPosition: PlaylistAddPosition) => void;
  excludeMediaIds: number[];
  /** Playlist mediaType — determines the source library (videos vs live streams). */
  mediaType: MediaType;
}

const MAX_RESULTS = 50;

export function MediaPickerModal({
  opened,
  onClose,
  onAdd,
  excludeMediaIds,
  mediaType,
}: MediaPickerModalProps) {
  const { items, isLoading } = usePlaylistMedia(mediaType);
  const noun = PlaylistUtils.getMediaNoun(mediaType);
  const [searchText, setSearchText] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [addPosition, setAddPosition] = useState<PlaylistAddPosition>('end');

  const excludeSet = useMemo(() => new Set(excludeMediaIds), [excludeMediaIds]);

  // Filter by mediaType (safety net — the source is already type-matched) and
  // exclude already-in-playlist items
  const candidates = useMemo(() => {
    return items
      .map(v => v.media)
      .filter(m => m.mediaType === mediaType && !excludeSet.has(m.mediaId));
  }, [items, mediaType, excludeSet]);

  // Prepare search targets
  const prepared = useMemo(() => {
    return candidates.map(m => ({
      media: m,
      prepared: fuzzysort.prepare(m.title),
    }));
  }, [candidates]);

  // Apply search filter
  const { results, totalMatching } = useMemo(() => {
    if (!searchText.trim()) {
      const sliced = candidates.slice(0, MAX_RESULTS);
      return { results: sliced, totalMatching: candidates.length };
    }
    const matches = fuzzysort.go(searchText, prepared, {
      key: 'prepared',
      limit: MAX_RESULTS + 100,
    });
    return {
      results: matches.slice(0, MAX_RESULTS).map(m => m.obj.media),
      totalMatching: matches.length,
    };
  }, [searchText, candidates, prepared]);

  function toggleItem(mediaId: number) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(mediaId)) {
        next.delete(mediaId);
      } else {
        next.add(mediaId);
      }
      return next;
    });
  }

  function handleAdd() {
    onAdd(Array.from(selectedIds), addPosition);
    handleClose();
  }

  function handleClose() {
    setSearchText('');
    setSelectedIds(new Set());
    setAddPosition('end');
    onClose();
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={`Add ${noun.pluralTitle}`}
      size="lg"
    >
      <Stack gap="md">
        <TextInput
          placeholder={`Search ${noun.plural}...`}
          leftSection={<IconSearch size={16} />}
          value={searchText}
          onChange={(e) => setSearchText(e.currentTarget.value)}
        />

        {totalMatching > MAX_RESULTS && (
          <Text size="xs" c="dimmed">
            Showing first {MAX_RESULTS} of {totalMatching} results. Refine your search to see more.
          </Text>
        )}

        <ScrollArea h={400}>
          {isLoading ? (
            <Center py="xl">
              <Loader size="sm" />
            </Center>
          ) : results.length === 0 ? (
            <Text c="dimmed" ta="center" py="xl">
              No matching {noun.plural} found. Try a different search.
            </Text>
          ) : (
            <Stack gap={0}>
              {results.map(item => {
                const mediaId = item.mediaId;
                const isSelected = selectedIds.has(mediaId);
                const isActive = item.mediaStateType === 'active';
                return (
                  <Box
                    key={mediaId}
                    px="sm"
                    py="xs"
                    onClick={() => toggleItem(mediaId)}
                    style={{
                      cursor: 'pointer',
                      borderBottom: '1px solid var(--mantine-color-gray-2)',
                      backgroundColor: isSelected ? 'var(--mantine-primary-color-light)' : undefined,
                    }}
                  >
                    <Group gap="sm" wrap="nowrap">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => toggleItem(mediaId)}
                        onClick={(e) => e.stopPropagation()}
                        tabIndex={-1}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text size="sm" truncate="end">{item.title}</Text>
                        <Text size="xs" c="dimmed">{bcLong.getStr(mediaId)}</Text>
                      </div>
                      <Badge
                        size="xs"
                        variant="light"
                        color={isActive ? 'green' : 'gray'}
                      >
                        {isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </Group>
                  </Box>
                );
              })}
            </Stack>
          )}
        </ScrollArea>

        <Group justify="space-between">
          <Group gap="xs">
            <Text size="sm" c="dimmed">
              {selectedIds.size} selected
            </Text>
            <Text size="sm" c="dimmed" ml="md">Add to:</Text>
            <SegmentedControl
              size="xs"
              value={addPosition}
              onChange={(value) => setAddPosition(value as PlaylistAddPosition)}
              data={ADD_POSITION_OPTIONS}
            />
          </Group>
          <Group gap="sm">
            <Button variant="default" onClick={handleClose}>Cancel</Button>
            <Button onClick={handleAdd} disabled={selectedIds.size === 0}>
              Add Selected
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
